import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BotService } from 'src/app/core/services/bot.service';
import { ComponentsDashboardModule } from '../components/components-dashboard.module';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ChatHistory, ChatHistoryGroup, ChatHistoryHeader } from 'src/app/models/history.interface';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ChatbotliveService } from 'src/app/core/services/chatbot_live.service';
import { ButtonGlobotComponent } from 'src/app/shared/components/button-globot/button-globot.component';
import { InputTextModule } from 'primeng/inputtext';
import { environment } from 'src/environments/environment';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { SocketWebService } from 'src/app/core/services/socket-web.service';
import { UserInterface } from 'src/app/models/user.interface';
import { UserService } from 'src/app/core/services/user.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { LoggerService } from 'src/app/core/services/logger.service';

@Component({
  selector: 'globot-globot-live',
  standalone: true,
  imports: [
    CommonModule,
    ComponentsDashboardModule,
    FormsModule,
    AutoCompleteModule,
    ButtonGlobotComponent,
    InputTextModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    TooltipModule,
    DropdownModule,
  ],
  templateUrl: './globot-live.component.html',
  styleUrls: ['./globot-live.component.scss'],
})
export class GlobotLiveComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  searchTerm: string = '';
  isMobile: boolean = window.innerWidth < 800;
  selectedChatView: boolean = false;
  records: any[] = [];
  botId: string = '';
  originalRecords: any[] = [];
  userInControl: boolean = false;
  selectedChat?: ChatHistoryHeader;
  selectedChatHistory: ChatHistoryGroup[] = [];
  isLiveMessagingReady = false;
  currentQuestion: string = '';
  loadingButton = false;
  user?: UserInterface;
  showPlansDialog: boolean = false;
  showPlansDialogPreferences: boolean = false;
  channelFilter = 'Todos';
  channelFilters = ['Todos', 'WhatsApp', 'Instagram', 'Messenger', 'Web'];
  channelOptions = this.channelFilters.map((f) => ({ label: f, value: f }));
  showCustomerPanel = true;

  get filteredByChannel(): any[] {
    if (this.channelFilter === 'Todos') return this.records;
    return this.records.filter((r) => r.channel === this.channelFilter.toUpperCase());
  }

  constructor(
    private botService: BotService,
    private aRoute: ActivatedRoute,
    private ss: SpinnerService,
    private router: Router,
    private chatbotliveService: ChatbotliveService,
    private changeDetectorRef: ChangeDetectorRef,
    private socket: SocketWebService,
    private userService: UserService,
    private sanitizer: DomSanitizer,
    private messageService: MessageService,
    private logger: LoggerService
  ) {
    this.isLiveMessagingReady = environment.isLiveMessagingReady;
    this.botId = this.aRoute.snapshot.paramMap.get('id') ?? '';
    const sections = this.router.routerState.snapshot.url.split('/');
    this.botId = sections[3];
  }

  ngOnInit() {
    this.userService
      .getUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.user = user;
        this.loadRecordsWithDateRange();
        this.socket.connectToRoom(this.botId, () => this.loadRecordsWithDateRange());
      });

    this.chatbotliveService.customerData$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (!data || !this.selectedChat) return;
      if (data.correlation_id !== this.selectedChat.correlation_id) return;
      this.selectedChat = { ...this.selectedChat, name: data.name ?? this.selectedChat.name };
      const record = this.records.find((r) => r.correlation_id === data.correlation_id);
      if (record) {
        record.name = data.name;
        record.email = data.email;
        record.phone = data.phone;
        record.sender_requirement = data.sender_requirement;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.socket.disconnectRoom(this.botId);
  }

  loadRecordsWithDateRange() {
    this.botService
      .getRecords(this.botId, undefined, { lastHours: 24 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          this.records = value;
          this.originalRecords = [...this.records];

          const selectedCorrelationId = this.selectedChat?.correlation_id;
          if (selectedCorrelationId) {
            const selectedRecord = this.records.find(
              (r) => r.correlation_id === selectedCorrelationId
            );
            if (!selectedRecord) {
              this.selectedChat = undefined;
              this.selectedChatHistory = [];
              this.selectedChatView = false;
              this.userInControl = false;
            } else {
              this.userInControl = selectedRecord.user_in_control != null;
              this.botService
                .getChatHistory(this.botId, selectedCorrelationId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (chatHistory: any) => {
                    this.selectedChatHistory = this.groupChatsByDate(chatHistory ?? []);
                  },
                  error: (error) => {
                    this.logger.error('Error al cargar historial', error);
                    this.selectedChatHistory = [];
                  },
                });
            }
          }
        },
        error: (error) => {
          this.logger.error('Error al cargar registros', error);
        },
      });
  }
  /**
   * GLOB-1888: tolera `null`/`undefined`. El backend respondía `204` para las
   * conversaciones sin mensajes y Angular entrega `null` al subscriber, con lo que
   * `[...chats]` tiraba TypeError y el panel quedaba en blanco sin ningún aviso.
   */
  private groupChatsByDate(chats: ChatHistoryGroup[] | null | undefined): ChatHistoryGroup[] {
    const sortedChats = [...(chats ?? [])];
    sortedChats.forEach((chat) => {
      try {
        chat.messages = chat.messages.slice().reverse();
      } catch (error) {
        this.logger.error('Fecha inválida omitida', chat.date);
      }
    });
    return sortedChats;
  }

  onChatHistorySelected(chatHistory: any) {
    this.selectedChat = chatHistory.record;
    this.userInControl = chatHistory.record.user_in_control != null;
    this.selectedChatHistory = this.groupChatsByDate(chatHistory.chatHistory);
    this.selectedChatView = true;
    this.showCustomerPanel = true;
    this.updateChat();

    // Limpiar el campo de entrada cuando se cambia de conversación
    this.currentQuestion = '';

    this.chatbotliveService.updateCustomerData(chatHistory.record);
  }

  takeControl(take_control: boolean = true) {
    this.loadingButton = true;

    if (!this.selectedChat?.correlation_id || !this.user) {
      this.loadingButton = false;
      return;
    }

    const payload = {
      correlation_id: this.selectedChat.correlation_id,
      take_manual_control: take_control,
    };

    this.chatbotliveService
      .takeControl(this.botId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.userInControl = take_control;
          this.loadingButton = false;
          if (this.selectedChat) {
            this.updateUserControlStatus(
              this.selectedChat.correlation_id,
              take_control,
              this.user!
            );
          }
        },
        error: () => {
          this.loadingButton = false;
        },
      });
  }

  updateChat() {
    const correlation_id = this.selectedChat?.correlation_id!;
    if (this.selectedChat?.new_message == false) {
      return;
    }
    this.chatbotliveService
      .updateChatHeader(this.botId, correlation_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {},
      });
  }

  sendAnswer() {
    if (this.currentQuestion.length === 0) return;
    const currentAnswer = this.currentQuestion;
    const correlation_id = this.selectedChat?.correlation_id!;
    const channel = this.selectedChat?.channel!;
    this.chatbotliveService
      .sendAnswer(this.botId, correlation_id, currentAnswer)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const newMessage: ChatHistory = {
            response: currentAnswer,
            channel: channel,
            created_at: new Date().toISOString(),
            correlation_id: correlation_id,
            answered_by: 'USER',
            user_in_control: this.user
              ? {
                  email: this.user.email,
                  name: this.user.name,
                  picture: this.user.picture,
                }
              : undefined,
          };
          const today = new Date();
          const formattedDate = today.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          });
          // Buscar o crear el grupo para hoy
          let group = this.selectedChatHistory.find((g) => g.date === formattedDate);
          if (!group) {
            group = { date: formattedDate, messages: [] };
            this.selectedChatHistory.unshift(group);
          }

          group.messages.unshift(newMessage);
          this.currentQuestion = '';
          this.changeDetectorRef.detectChanges();
        },
        error: (err) => {
          if ([400, 404, 429, 500].includes(err.status)) {
            this.messageService.add({
              severity: 'error',
              summary: 'Ocurrió un error inesperado',
              detail: 'Por favor, inténtalo más tarde',
            });
          }
        },
      });
  }
  updateUserControlStatus(correlation_id: string, userInControl: boolean, user: UserInterface) {
    const record = this.records.find((r) => r.correlation_id === correlation_id);
    if (record) {
      if (userInControl) {
        record.user_in_control = record.user_in_control || {
          name: user.name,
          picture: user.picture,
        };
      } else {
        record.user_in_control = null;
      }
    }
  }
  getChannelIcon(channel: string): string {
    const map: Record<string, string> = {
      WEB: 'pi pi-globe',
      WHATSAPP: 'pi pi-whatsapp',
      INSTAGRAM: 'pi pi-instagram',
      MESSENGER: 'pi pi-facebook',
      FACEBOOK: 'pi pi-facebook',
      DISCORD: 'pi pi-discord',
      SLACK: 'pi pi-slack',
    };
    return map[(channel || '').toUpperCase()] ?? 'pi pi-comments';
  }

  getChannelColor(channel: string): string {
    const map: Record<string, string> = {
      WEB: '#3B82F6',
      WHATSAPP: '#22C55E',
      INSTAGRAM: '#EC4899',
      MESSENGER: '#0084FF',
      FACEBOOK: '#1877F2',
      DISCORD: '#5865F2',
      SLACK: '#E01E5A',
    };
    return map[(channel || '').toUpperCase()] ?? '#6D5BE6';
  }

  getChannelLabel(channel: string): string {
    const labels: Record<string, string> = {
      WHATSAPP: 'WhatsApp',
      MESSENGER: 'Messenger',
      INSTAGRAM: 'Instagram',
      WEB: 'Web',
    };
    return labels[channel] || channel;
  }

  isChannelIconVisible(channel: string): boolean {
    return ['WHATSAPP', 'MESSENGER', 'INSTAGRAM', 'WEB'].includes(channel);
  }
  formatMessage(text: string) {
    let formattedText = text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/ - /g, '<br>- ')
      .replace(/(\d{4}-\d{2}-\d{2})/g, '<span class="highlight-date">$1</span>')
      .replace(/(\d{1,3}(?:\.\d{3})*(?:,\d+)?)/g, '<span class="highlight-number">$1</span>');

    return this.sanitizer.sanitize(SecurityContext.HTML, formattedText);
  }
  onPreferencesSaved(_selectedTime: number) {
    this.showPlansDialogPreferences = false;
  }

  trackByGroupDate(_index: number, group: ChatHistoryGroup): string {
    return group.date;
  }

  trackByIndex(index: number): number {
    return index;
  }

  onPreferencesCancelled() {
    this.showPlansDialogPreferences = false;
  }
  isMetaLimitExceeded(): boolean {
    if (!this.selectedChatHistory?.length) return false;

    const metaChannels = ['WHATSAPP', 'MESSENGER', 'INSTAGRAM'];
    let lastUserPromptMessage: any = null;

    // Primer loop simplificado
    for (const group of this.selectedChatHistory) {
      const validMessages = group.messages.filter(
        (m) => m.user_prompt !== null && metaChannels.includes(m.channel)
      );

      if (!validMessages.length) continue;

      const groupLastMessage = validMessages.reduce((latest, current) =>
        new Date(current.created_at) > new Date(latest.created_at) ? current : latest
      );

      if (
        !lastUserPromptMessage ||
        groupLastMessage.created_at > lastUserPromptMessage.created_at
      ) {
        lastUserPromptMessage = groupLastMessage;
      }
    }

    if (!lastUserPromptMessage) return false;

    const messageDate = Date.parse(
      lastUserPromptMessage.created_at
        .replace(/(\.\d+)?([+-]\d{2}:?\d{2})$/, '.000$2')
        .replace(' ', 'T')
    );

    if (isNaN(messageDate)) return false;

    return Date.now() - messageDate >= 86400000; // 24h en milisegundos
  }
}
